import React, { useCallback, useEffect, useState } from "react";
import { t } from "i18next";
import { motion } from "framer-motion";
import Proximiio from "proximiio-js-library";
import {
  osName,
  browserName,
  mobileModel,
  mobileVendor,
  deviceType,
} from "react-device-detect";
import { centerOfMass } from "@turf/center-of-mass";
import { cn, filterByAmenity, getSortedPOIs } from "@/lib/utils";
import { Accordion } from "@/components/ui/accordion";
import FilterPicker from "./FilterPicker";
// import MoreAccordion from "./MoreAccordion";
// import QRCode from "./QRCode";

import useMapStore from "@/store/mapStore";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import {
  FilterCategoryModel,
  FilterItemModel,
} from "@/models/filterItem.model";
import useFeaturesStore from "@/store/featuresStore";

function Filters() {
  const [value, setValue] = useState<string>("");

  const filterCategories = useMapStore((state) => state.filterCategories);
  const map = useMapStore((state) => state.map);
  const setActiveFilter = useMapStore((state) => state.setActiveFilter);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const activeCategory = useMapStore((state) => state.activeCategory);
  const kioskMode = useMapStore((state) => state.kioskMode);
  const activeKiosk = useMapStore((state) => state.activeKiosk);
  const currentLang = useMapStore((state) => state.currentLang);
  const appSession = useMapStore((state) => state.appSession);
  const setActiveCategory = useMapStore((state) => state.setActiveCategory);
  const features = useFeaturesStore((state) => state.features);
  const floors = useMapStore((state) => state.floors);
  const currentFloor = useMapStore((state) => state.currentFloor);
  const verticalKioskMode = useMapStore((state) => state.verticalKioskMode);
  const kioskScaling = useMapStore((state) => state.kioskScaling);

  const pois = getSortedPOIs({ features, currentLang, floors });

  const categoryChangeHandler = useCallback(
    (value: string) => {
      // setIsLoading(true);
      setValue(value);

      const filterCategory = filterCategories.find((i) => i.id === value);

      if (Object.keys(map).length === 0) {
        return;
      }

      map.enablePolygonPreventedIcons();

      if (!filterCategory) {
        setActiveFilter({} as FilterItemModel);
        setActiveCategory({} as FilterCategoryModel);
        return;
      }

      console.log("filter category", filterCategory);

      if (filterCategory.id === "live-queue-times") {
        features.map((f) => {
          if (f.properties?._queue?.queueAmenityId) {
            f.properties.amenity = f.properties?._queue?.queueAmenityId;
            map.updateFeature(
              f.id,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              { amenity: f.properties.amenity },
              true,
            );
          }
        });
      } else {
        features.map((f) => {
          if (f.properties?._queue?.amenityId) {
            f.properties.amenity = f.properties?._queue?.amenityId;
            map.updateFeature(
              f.id,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              { amenity: f.properties.amenity },
              true,
            );
          }
        });
      }

      setActiveCategory(filterCategory);

      if (
        !mobileMode &&
        !verticalKioskMode &&
        filterCategory.items.filter((i) => i.listed !== false).length === 1
      ) {
        setActiveFilter(filterCategory.items[0]);
      }

      const categoryAmenities = filterCategory.items
        .filter((i) => i.id && i.id?.length > 0 && i.listed !== false)
        .map((i) => i.id as string | string[])
        .flat(2);

      map.resetAmenityFilters();
      map.setAmenityFilter(categoryAmenities);
      map.disablePolygonPreventedIcons();

      // filter pois by category and change level in case there are no pois withing the category on current level
      const filteredPois = filterByAmenity(
        pois,
        filterCategory.items.map((i) => (i.id ? i.id : [])).flat(2),
      );

      if (filteredPois.length > 0) {
        const sameLevelFeatures = filteredPois.filter(
          (i) => i.properties.level === currentFloor.level,
        );
        if (sameLevelFeatures.length === 0) {
          const floor = floors.find(
            (f) => f.level === filteredPois[0].properties.level,
          );
          if (floor) {
            map.setFloorById(floor.id);
          }
        }
        const centerPoint = centerOfMass({
          type: "FeatureCollection",
          features:
            sameLevelFeatures.length > 0 ? sameLevelFeatures : filteredPois,
        });
        if (mobileMode) {
          map.getMapboxInstance().jumpTo({
            center: centerPoint.geometry.coordinates as [number, number],
            zoom: 18.5,
            padding: {
              top: 0,
              bottom: 370,
              left: 0,
              right: 0,
            },
          });
        } else {
          map.getMapboxInstance().jumpTo({
            center: centerPoint.geometry.coordinates as [number, number],
          });
        }
      }

      if (filterCategory.listed !== false) {
        //save amenity category select log
        console.log("LOG: save amenity category select log");
        const userData = {
          osName,
          browserName,
          mobileModel,
          mobileVendor,
          deviceType:
            kioskMode && !mobileMode && activeKiosk?.name
              ? activeKiosk?.name
              : deviceType,
        };
        new Proximiio.InteractionLogger({
          interactionType: "select",
          targetElementType: "amenity_category",
          targetElementTitle: t(filterCategory.title, { lng: "en" }),
          targetElementId: Array.isArray(filterCategory.id)
            ? filterCategory.id.join("_")
            : filterCategory.id
              ? filterCategory.id
              : undefined,
          kioskId: activeKiosk?.id ? activeKiosk?.id : undefined,
          kioskTitle: activeKiosk?.name ? activeKiosk?.name : undefined,
          metadata: userData,
          language: currentLang,
          session: appSession,
        });
      }

      if (mobileMode) {
        map.getMapboxInstance().jumpTo({
          center: [-6.460295186, 53.545827829],
          zoom: 16,
          padding: {
            top: 0,
            bottom: 370,
            left: 0,
            right: 0,
          },
        });
      }

      /*setTimeout(() => {
        setIsLoading(false);
      }, 1500);*/
    },
    [
      filterCategories,
      map,
      activeKiosk,
      currentLang,
      appSession,
      kioskMode,
      mobileMode,
      setActiveCategory,
      setActiveFilter,
      currentFloor.level,
      floors,
      pois,
      verticalKioskMode,
    ],
  );

  useEffect(() => {
    if (mobileMode) {
      categoryChangeHandler(
        activeCategory?.id
          ? activeCategory.id
          : filterCategories.filter((i) => i.listed !== false)[0].id,
      );
    } else {
      categoryChangeHandler(activeCategory?.id);
    }
  }, []);

  useEffect(() => {
    if (!activeCategory?.id && !mobileMode) {
      setValue(filterCategories.filter((i) => i.listed !== false)[0].id);
      setActiveFilter({} as FilterItemModel);
    }
    if (verticalKioskMode && activeCategory?.id === "more") {
      setValue("more");
    }
    if (verticalKioskMode && activeCategory?.id) {
      setValue(activeCategory?.id);
    }
  }, [activeCategory, setActiveFilter, mobileMode, verticalKioskMode]);

  if (mobileMode) {
    return (
      <>
        <motion.div
          key="filters"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          transition={{ duration: 0.2 }}
          className="absolute top-[calc(100dvh-384px)] z-100 h-96 w-full rounded-t-2xl bg-white p-6 shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)]"
          style={{
            //@ts-expect-error color
            "--custom-handle-bg": `${activeCategory.color}`,
          }}
        >
          <OverlayScrollbarsComponent
            className={cn("pt-10")}
            options={{
              scrollbars: { autoHide: "move", autoHideDelay: 3000 },
            }}
            defer
          >
            <ul className="mb-5 flex gap-2">
              {filterCategories
                .filter((i) => i.listed !== false)
                .map((category) => (
                  <li
                    key={category.id}
                    style={{
                      //@ts-expect-error color
                      "--category-color": `hsl(${category.color})`,
                    }}
                    className={cn(
                      "flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full bg-primary px-4 py-2 text-xs text-white",
                      category.color && "bg-[var(--category-color)]",
                      value === category.id
                        ? "opacity-1"
                        : "opacity-50 hover:opacity-80",
                    )}
                    onClick={() => categoryChangeHandler(category.id)}
                  >
                    <div className="mr-0.5 flex h-5 w-4 text-lg">
                      {category.icon && React.createElement(category.icon)}
                      {category.iconImage && (
                        <img
                          src={
                            category.iconImageAlt
                              ? category.iconImageAlt
                              : category.iconImage
                          }
                          alt={t(category.title)}
                          className="max-h-5 w-4"
                        />
                      )}
                    </div>
                    {t(category.title)}
                  </li>
                ))}
            </ul>
          </OverlayScrollbarsComponent>
          {activeCategory?.id && (
            <FilterPicker
              key={activeCategory.title}
              heading={t(activeCategory.title)}
              id={activeCategory.id}
              items={activeCategory.items}
              color={activeCategory.color}
            />
          )}
        </motion.div>
      </>
    );
  }

  if (verticalKioskMode) {
    return (
      <motion.div
        key="filters"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0 }}
        transition={{ duration: 0.2 }}
        className={cn(
          "fixed bottom-[calc(28%-80px)] left-[5%] top-auto z-100 w-[90%]",
          !kioskScaling &&
            "kiosk:!left-[2%] kiosk:bottom-[calc(28%-175px)] kiosk:origin-bottom-left kiosk:scale-150",
        )}
        style={{
          //@ts-expect-error color
          "--custom-handle-bg": `${activeCategory.color}`,
        }}
      >
        <OverlayScrollbarsComponent
          className={cn("relative z-10 w-[calc(100%-50px)] px-5")}
          options={{
            scrollbars: { autoHide: "move", autoHideDelay: 3000 },
          }}
          defer
        >
          <ul
            className={cn(
              "flex gap-2 py-2",
              !kioskScaling &&
                "kiosk:w-[64%] kiosk:origin-bottom-left kiosk:scale-150 kiosk:pt-10",
            )}
          >
            {filterCategories
              .filter((i) => i.listed !== false)
              .map((category) => (
                <li
                  key={category.id}
                  style={{
                    //@ts-expect-error color
                    "--category-color": `hsl(${category.color})`,
                  }}
                  className={cn(
                    "flex cursor-pointer items-center gap-0.5 whitespace-nowrap rounded-full bg-primary px-4 py-3 text-base text-white shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)]",
                    category.color && "bg-[var(--category-color)]",
                    !activeCategory?.id || value === category.id
                      ? "opacity-1"
                      : "opacity-50 shadow-none hover:opacity-80",
                  )}
                  onClick={() => categoryChangeHandler(category.id)}
                >
                  <div className="flex h-7 w-6 items-center text-lg group-data-[dir=ltr]:mr-1 group-data-[dir=rtl]:ml-1 kiosk:text-xl">
                    {category.icon && React.createElement(category.icon)}
                    {category.iconImage && (
                      <img
                        src={
                          category.iconImageAlt
                            ? category.iconImageAlt
                            : category.iconImage
                        }
                        alt={t(category.title)}
                        className="max-h-7 w-6"
                      />
                    )}
                  </div>
                  {t(category.title)}
                </li>
              ))}
          </ul>
        </OverlayScrollbarsComponent>
        {activeCategory?.id && (
          <FilterPicker
            key={activeCategory.title}
            heading={t(activeCategory.title)}
            id={activeCategory.id}
            items={activeCategory.items}
            color={activeCategory.color}
          />
        )}
        {/*<motion.div
            key="filters"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.2 }}
            className={cn("", activeCategory?.id && "w-[460px]")}
          >
            <Accordion
              type="single"
              value={value}
              onValueChange={categoryChangeHandler}
              collapsible
              className="pointer-events-auto flex gap-2"
            >
              {filterCategories
                .filter((i) => i.listed !== false)
                .map((category) => (
                  <FilterPicker
                    key={category.title}
                    heading={t(category.title)}
                    id={category.id}
                    items={category.items}
                    color={category.color}
                    isOpen={value === category.id}
                  />
                ))}
            </Accordion>
            {<MoreAccordion />}
          </motion.div>
        */}
      </motion.div>
    );
  }

  return (
    <div
      className={cn("h-full")}
      style={{
        //@ts-expect-error color
        "--custom-handle-bg": `var(--primary)`,
      }}
    >
      <OverlayScrollbarsComponent
        className={cn("h-full", kioskMode && "overflow-hidden")}
        options={{
          scrollbars: { autoHide: "move", autoHideDelay: 3000 },
          overflow: { x: "hidden", y: kioskMode ? "hidden" : "scroll" },
        }}
        defer
      >
        <div className={cn("h-full")}>
          <motion.div
            key="filters"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.2 }}
            className="flex h-full flex-col px-5 kiosk:items-center kiosk:justify-center"
          >
            <Accordion
              type="single"
              value={value}
              onValueChange={categoryChangeHandler}
              collapsible
              className="w-full flex-1"
            >
              {filterCategories
                .filter((i) => i.listed !== false)
                .map((category) => (
                  <FilterPicker
                    key={category.title}
                    heading={t(category.title)}
                    id={category.id}
                    items={category.items}
                    color={category.color}
                    isOpen={value === category.id}
                  />
                ))}
            </Accordion>
            {/*<MoreAccordion />*/}
            {/*<QRCode size={180} className="mt-5" />*/}
          </motion.div>
        </div>
      </OverlayScrollbarsComponent>
    </div>
  );
}

export default Filters;
